import { Schema } from "effect";

import { ResolvedDashboardWidget } from "./dashboardArtifacts";
import {
  Identifier,
  IsoDate,
  IsoTimestamp,
  NonNegativeCount,
  PositiveCount,
  ShortText,
} from "./dashboardSchemaPrimitives";
import { ChangeExplanation, MetricValueStatus, Provenance } from "./metrics";

/**
 * One request loads a whole dashboard: `POST
 * /api/v1/dashboards/artifacts/:ref/resolve` with a `DashboardResolveRequest`
 * body answers with NDJSON (`application/x-ndjson`), one
 * `DashboardResolveWidgetLine` per data widget as soon as it resolves (in any
 * order), then one `DashboardResolveDoneLine`. Text widgets have no line.
 */

export const DASHBOARD_RESOLVE_ROUTE = "/api/v1/dashboards/artifacts/:ref/resolve";
export const DASHBOARD_RESOLVE_CONTENT_TYPE = "application/x-ndjson";

/** A date range is `{ start, end }`, a select is text, a multi-select is a text array. */
export const DashboardParameterValue = Schema.Union([
  Schema.String,
  Schema.Array(Schema.String),
  Schema.Struct({ start: IsoDate, end: IsoDate }),
]);
export type DashboardParameterValue = typeof DashboardParameterValue.Type;

/** Without `versionId`, the published version (or the latest draft) resolves. */
export const DashboardResolveRequest = Schema.Struct({
  versionId: Schema.optionalKey(Schema.String.check(Schema.isMinLength(1))),
  /** Parameter values by parameter id; a missing parameter takes its default. */
  parameters: Schema.Record(Identifier, DashboardParameterValue),
});
export type DashboardResolveRequest = typeof DashboardResolveRequest.Type;

/**
 * How fresh a widget's numbers are. `computedAt` is the source table's last
 * successful run (for raw-replica SQL, when the query ran). A settling widget
 * may still change; `settlingFrom` marks the first settling period, so a chart
 * can mark each point. `reportedAtClose` is a KPI's number as first reported
 * when its period closed. `cadenceMinutes` is how often the source table is
 * rebuilt; numbers older than twice that are stale. Without it, clients assume
 * a daily rebuild.
 */
export const DashboardFreshness = Schema.Struct({
  computedAt: IsoTimestamp,
  cadenceMinutes: Schema.optionalKey(PositiveCount),
  status: MetricValueStatus,
  settlingReason: Schema.optionalKey(ShortText),
  settlingFrom: Schema.optionalKey(IsoDate),
  reportedAtClose: Schema.optionalKey(
    Schema.Struct({ value: Schema.NullOr(Schema.Finite), computedAt: IsoTimestamp }),
  ),
});
export type DashboardFreshness = typeof DashboardFreshness.Type;

/**
 * One widget's data and where it came from: one provenance entry per metric in
 * its query, or one ad-hoc entry. Errors carry no freshness.
 */
export const DashboardWidgetResolutionFields = {
  widgetId: Identifier,
  resolved: ResolvedDashboardWidget,
  provenance: Schema.Array(Provenance).check(Schema.isMinLength(1)),
  freshness: Schema.optionalKey(DashboardFreshness),
  /** Why a closed period's number moved since it was reported. */
  explanation: Schema.optionalKey(ChangeExplanation),
};

/** The resolution checks shared by stream lines and assistant previews. */
export const dashboardWidgetResolutionFilter = Schema.makeFilter(
  (line: {
    readonly widgetId: string;
    readonly resolved: ResolvedDashboardWidget;
    readonly freshness?: DashboardFreshness;
  }) => {
    if (line.resolved.widgetId !== line.widgetId) {
      return { path: ["resolved", "widgetId"], message: "Must match the line's widgetId" };
    }
    if (line.resolved.kind !== "error" && !line.freshness) {
      return { path: ["freshness"], message: "Resolved widgets need freshness" };
    }
    return undefined;
  },
  { identifier: "DashboardWidgetResolution" },
);

export const DashboardResolveWidgetLine = Schema.Struct(DashboardWidgetResolutionFields).check(
  dashboardWidgetResolutionFilter,
);
export type DashboardResolveWidgetLine = typeof DashboardResolveWidgetLine.Type;

/** Closes the stream; `errors` counts widget lines whose `resolved.kind` is `error`. */
export const DashboardResolveDoneLine = Schema.Struct({
  done: Schema.Literal(true),
  errors: NonNegativeCount,
});
export type DashboardResolveDoneLine = typeof DashboardResolveDoneLine.Type;

export const DashboardResolveStreamLine = Schema.Union([
  DashboardResolveWidgetLine,
  DashboardResolveDoneLine,
]);
export type DashboardResolveStreamLine = typeof DashboardResolveStreamLine.Type;
